/**
 * Perimeter Heckle — the character cast + chatter→character assignment
 * (docs/specs/HECKLE-PERIMETER-PRD.md, ADR-0034 "character cast = chatter
 * identity").
 *
 * The manifest (lib/heckle-cast-manifest.json, regenerated by the pack script
 * once real art lands) lists back-view sprites served from /public/heckle-cast.
 * A stable hash maps each roster key to a PERMANENT character — the same
 * chatter is always the same character, like Twitch chat colors. Pure CJS, no
 * ambient randomness, so assignment is testable and identical on server and
 * client.
 *
 * `overrides` is the seam the viewer economy fills (E3, VIEWER-ECONOMY-PRD
 * §7): a map of rosterKey → manifest-shaped entry that wins over the hash.
 * The overlay builds it from viewers' GLOBAL character locks (a lock must be
 * an OWNED character — lib/points-collection.js setLock validates that).
 */

const manifest = require('./heckle-cast-manifest.json');
const { hashFrac } = require('./stable-hash');

// Salt is fixed and independent of the GA-scatter salts (3/7/11) so a
// chatter's character never correlates with their scatter position.
const CAST_SALT = 17;

const CAST = manifest.characters;

// id → entry, derived once at load (the manifest is static per process).
const CAST_INDEX = new Map(CAST.map((c) => [c.id, c]));

// Display names derive from the slug (title-cased, hyphens → spaces); these
// few read wrong mechanically and get a hand override. Lives HERE, not in the
// manifest — the cast-packer regenerates that file and would clobber edits.
const DISPLAY_NAME_OVERRIDES = {
  'crt-robot': 'CRT Robot',
  'fastfood-cup': 'Fast-Food Cup',
  honeybadger: 'Honey Badger',
  boxknight: 'Box Knight',
};

/** The permanent character for a roster key. `cast` defaults to the packed
 *  manifest; `overrides` (rosterKey → entry) is the lock seam (E3). */
function characterFor(rosterKey, cast = CAST, overrides = {}) {
  const custom = overrides[rosterKey];
  if (custom) return custom;
  const list = cast && cast.length ? cast : CAST;
  return list[Math.floor(hashFrac(String(rosterKey || ''), CAST_SALT) * list.length) % list.length];
}

/** Manifest entry by character id — null for anything not in the cast (the
 *  pull/lock validators' membership check). */
function characterById(id) {
  return CAST_INDEX.get(id) || null;
}

/** Human-readable character name for reveal badges ("★ CAUGHT CRT ROBOT!")
 *  and the E4 collection grid. Unknown ids title-case anyway (never blank). */
function displayNameFor(id) {
  const slug = String(id || '');
  if (DISPLAY_NAME_OVERRIDES[slug]) return DISPLAY_NAME_OVERRIDES[slug];
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');
}

module.exports = { CAST, MANIFEST_VERSION: manifest.version, characterFor, characterById, displayNameFor, hashFrac };
